import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const REQUEST_LABELS = {
  request_customization: "Request Customization",
  schedule_demo: "Schedule a Demo",
};

export default function ContactRequestDialog({ open, onOpenChange, requestType = "schedule_demo", system }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        ...form,
        request_type: requestType,
        system_id: system?.id,
        system_name: system?.name,
      });
      toast.success("Thanks — our team will reach out within 1 business day.");
      onOpenChange(false);
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="contact-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{REQUEST_LABELS[requestType]}</DialogTitle>
          <DialogDescription>
            {system ? `For ${system.name}.` : ""} Tell us a bit about your business — we'll reach out shortly.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cr-name">Full name *</Label>
              <Input id="cr-name" data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="cr-email">Work email *</Label>
              <Input id="cr-email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="cr-company">Company</Label>
              <Input id="cr-company" data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="cr-phone">Phone</Label>
              <Input id="cr-phone" data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <div>
            <Label htmlFor="cr-msg">Message</Label>
            <Textarea id="cr-msg" data-testid="contact-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <Button type="submit" disabled={submitting} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="contact-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Submit Request
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
